"use client";

import React, { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import { ArrowUpDown, Eye, Edit, Plus, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Spinner } from "../components/ui/shadcn-io/spinner";

// Updated type with off-boarding fields
export type SalaryEmployee = {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  designation?: string;
  department?: string;
  employeeType?: "Employee" | "Intern" | "Part-Time";
  salaryStatus: "added" | "not_added";
  isActive: boolean;
  resignationDate?: string;
  salary?: {
    basic: number;
    hra: number;
    otherAllowances: number;
    total: number;
    deductions: {
      pf: number;
      esi: number;
    };
  };
};

// Helper function to get badge color based on employee type
const getEmployeeTypeBadgeColor = (type?: string) => {
  switch (type) {
    case "Intern":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "Part-Time":
      return "bg-purple-100 text-purple-800 border-purple-200";
    case "Employee":
    default:
      return "bg-green-100 text-green-800 border-green-200";
  }
};

const ActionsCell = ({ employee }: { employee: SalaryEmployee }) => {
  const [isNavigating, setIsNavigating] = useState(false);
  const [actionType, setActionType] = useState<"add" | "view" | "edit" | null>(
    null
  );

  const handleAdd = async () => {
    setIsNavigating(true);
    setActionType("add");
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      window.location.href = `/salary/add/${employee.id}`;
    } finally {
      setIsNavigating(false);
      setActionType(null);
    }
  };

  const handleEdit = async () => {
    setIsNavigating(true);
    setActionType("edit");
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      window.location.href = `/salary/edit/${employee.id}`;
    } finally {
      setIsNavigating(false);
      setActionType(null);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {employee.salaryStatus === "not_added" ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-8 w-8 p-0"
              disabled={isNavigating}
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleAdd} disabled={isNavigating}>
              {isNavigating && actionType === "add" ? (
                <Spinner variant="ellipsis" size={16} className="mr-2" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {isNavigating && actionType === "add"
                ? "Loading..."
                : "Add Salary"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-8 w-8 p-0"
              disabled={isNavigating}
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleEdit} disabled={isNavigating}>
              {isNavigating && actionType === "edit" ? (
                <Spinner variant="ellipsis" size={16} className="mr-2" />
              ) : (
                <Edit className="mr-2 h-4 w-4" />
              )}
              {isNavigating && actionType === "edit"
                ? "Loading..."
                : "Edit Salary"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export const salaryColumns: ColumnDef<SalaryEmployee>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Employee Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const name = row.getValue("name") as string;
      return <div className="font-medium text-left">{name}</div>;
    },
  },
  {
    accessorKey: "employeeType",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Type
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const type = row.getValue("employeeType") as string;
      return (
        <div className="text-left">
          <span
            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border ${getEmployeeTypeBadgeColor(
              type
            )}`}
          >
            {type || "Employee"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "email",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Email
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const email = row.getValue("email") as string;
      return <div className="text-left">{email}</div>;
    },
  },
  {
    accessorKey: "designation",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Designation
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const designation = row.getValue("designation") as string;
      return <div className="text-left">{designation || "N/A"}</div>;
    },
  },
  {
    accessorKey: "isActive",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Employee Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const isActive = row.getValue("isActive") as boolean;
      const resignationDate = row.original.resignationDate;

      return (
        <div className="text-left">
          {isActive ? (
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-green-100 text-green-800">
              Active
            </span>
          ) : (
            <div className="space-y-1">
              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-red-100 text-red-800">
                Inactive
              </span>
              {resignationDate && (
                <div className="text-xs text-muted-foreground">
                  {new Date(resignationDate).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "salaryStatus",
    header: "Salary Status",
    cell: ({ row }) => {
      const status = row.getValue("salaryStatus") as string;
      return (
        <div className="flex items-center">
          {status === "added" ? (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              Added
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
              Not Added
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const employee = row.original;
      return <ActionsCell employee={employee} />;
    },
  },
];
